import {
    RiArrowDownLine, RiArrowDropLeftFill, RiArrowDropRightFill, RiArrowUpLine,
} from "@remixicon/react";
import { } from "native-injects";
import React, { ReactNode, useMemo, useState } from "react";
import {
    FormFieldType,
    MetricsDashboardData,
    MetricsDashboardDataColumn,
    MetricsDashboardGraph,
    MetricsDashboardGraphDataSource,
    MetricsDashboardGraphDimensionFormat,
    MetricsDashboardGraphTypeDisplayType, MetricsDashboardSortDirection,
} from "../../../__generated__/api";
import ReportFieldSelector, { useFlatFields } from "../../../components/pickers/field-selector/ReportFieldSelector";
import Select from "../../../components/select/Select";
import Button from "../../../components/theme/button/Button";
import FormGroup from "../../../components/theme/form-group/FormGroup";
import SortUpDown, { Direction } from "../../../components/theme/sortable/SortBy";
import { useTheme } from "../../../contexts/Theme";
import Styles from "../Reports.module.scss";
import {
    FIELDS,
    GraphProps, GraphSettingsProperties, compareValues, filter, formatDate, formatValueWithDisplay, getMetricValue,
    getSortValue,
} from "./base";
import ColumnProperties from "./Column";
import Filters from "./Filters";
import Title from "./Title";
import { Link, Paths } from "../../../contexts/Routing";

const SORT_FIELDS = {
    [MetricsDashboardSortDirection.Ascending]: {
        name: "Descending",
        icon: <RiArrowDownLine className={Styles["sorting-icon"]} />,
    },
    [MetricsDashboardSortDirection.Descending]: {
        name: "Ascending",
        icon: <RiArrowUpLine className={Styles["sorting-icon"]} />,
    },
};

function SortField({
    field, direction, index, title, onChange, graph, dataSource,
}: {
    direction: MetricsDashboardSortDirection;
    field: MetricsDashboardDataColumn | undefined;
    index: number;
    title: string;
    dataSource: MetricsDashboardGraphDataSource;
} & GraphSettingsProperties) {
    const directionValue = SORT_FIELDS[direction];
    return (
        <div className={Styles["table-edit-sorting-line"]}>
            <FormGroup
                label={title}
                size="small"
                helpDisplay="icon"
            >
                <ReportFieldSelector
                    value={field}
                    dataSource={dataSource}
                    isClearable
                    onChange={value => {
                        const copy = Array.from(graph.sort ?? []);
                        copy[index] = {
                            ...copy[index] ?? {},
                            fieldId: value?.field.id,
                        };
                        onChange({
                            ...graph,
                            sort: copy,
                        });
                    }}
                />
            </FormGroup>
            <FormGroup
                label="Type"
                size="small"
                helpDisplay="icon"
            >
                <Select
                    value={{
                        value: direction,
                        data: direction,
                        label: (
                            <div className={Styles["widget-type"]}>
                                {directionValue.icon}
                                {directionValue.name}
                            </div>
                        ),
                    }}
                    onChange={selectedDirection => {
                        const copy = Array.from(graph.sort ?? []);
                        copy[index] = {
                            ...copy[index],
                            direction: selectedDirection.value as MetricsDashboardSortDirection,
                        };
                        onChange({
                            ...graph,
                            sort: copy,
                        });
                    }}
                    options={Object.entries(SORT_FIELDS).map(([key, value]) => ({
                        value: key,
                        data: value.name,
                        label: (
                            <div className={Styles["widget-type"]}>
                                {value.icon}
                                {value.name}
                            </div>
                        ),
                    }))}
                />
            </FormGroup>

        </div>
    );
}
export function TableProperties(props: GraphSettingsProperties) {
    const sortFields = props.graph.sort;

    const { flatFields } = useFlatFields(props.graph.dataSource);

    const firstSort = sortFields?.[0] ? flatFields[sortFields[0].fieldId]?.column : undefined;
    const secondSort = sortFields?.[1] ? flatFields[sortFields[1].fieldId]?.column : undefined;

    return (

        <div>
            <ColumnProperties
                field="dimensions"
                label="Dimension Columns"
                helpText="This is the list of regular columns that will be displayed in the table."
                // canSetAggregation
                canAddLabel
                canAddFormat
                {...props}
            />
            <ColumnProperties
                field="metrics"
                label="Metric Columns"
                helpText="This is the list of aggregate metric columns that will be used to aggregate fields in the table."
                canSetAggregation
                canAddLabel
                canAddFormat
                {...props}
            />

            <SortField
                field={firstSort}
                direction={sortFields?.[0]?.direction ?? MetricsDashboardSortDirection.Descending}
                index={0}
                dataSource={props.graph.dataSource}
                title="Primary Sorting"
                {...props}
            />
            <SortField
                field={secondSort}
                direction={sortFields?.[1]?.direction ?? MetricsDashboardSortDirection.Descending}
                index={1}
                dataSource={props.graph.dataSource}
                title="Secondary Sorting"
                {...props}
            />

            <Filters isAdmin={props.isAdmin} graph={props.graph} onChange={props.onChange} />

        </div>
    );
}

interface ColumnType {
    fieldId: string;
    label?: string;
}

const PAGE_SIZE = 15;
const TYPE_TO_TYPE: Record<string, MetricsDashboardGraphTypeDisplayType> = {
    "urn:fq:form-field:timeToFirstResponseInHours": MetricsDashboardGraphTypeDisplayType.Duration,
    "urn:fq:form-field:timeToCloseInHours": MetricsDashboardGraphTypeDisplayType.Duration,
};

interface RowData {
    display: string | ReactNode;
    textDisplay: string | undefined;
    value: string | number | boolean | Date | undefined;
}

interface RowsColumns {
    graph: MetricsDashboardGraph;
    data: MetricsDashboardData;
    columnIndexMap: Record<string, number>;
    labelMap: Record<string, string>;
}

export function createRowsAndColumns(props: RowsColumns): { rowsData: RowData[][]; columns: string[]; } {
    const indexes = props.graph.dimensions?.map(row => ({
        columnIndex: props.columnIndexMap[row.fieldId],
        format: row.format,
    })) ?? [];

    const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

    const rows = props.graph.metrics?.isNotEmpty
        ? dataRows.groupBy(row => JSON.stringify(indexes.map(dimension => {
            const value = row.values[dimension.columnIndex];
            return dimension.format && value ? formatDate(new Date(value), dimension.format) : value;
        })))
        : dataRows.map(row => [row]);

    const colData = (props.graph.dimensions as ColumnType[] ?? [])
        .concat(props.graph.metrics as ColumnType[] ?? []);

    const cols = colData.map((column, index) => {
        if (column.label) {
            return column.label;
        }
        const columnIndex = props.columnIndexMap[column.fieldId];
        return props.data.columns[columnIndex]?.field.name ?? `Column ${index}`;
    });

    let rowData = Object.values(rows).map(group => {
        const first = group.firstElement;
        const dimensions = indexes.map(({ columnIndex, format }) => {
            const val = first.values[columnIndex];
            let value = props.labelMap?.[val ?? ""];
            if (value || !val) {
                return {
                    display: value ?? "-",
                    textDisplay: value ?? "-",
                    value: val,
                } as RowData;
            }
            let rawValue;
            const columnField = props.data.columns[columnIndex];
            const type = columnField.field?.type;
            if (type === FormFieldType.Boolean) {
                rawValue = val === "true";
                value = rawValue ? "Yes" : "No";
            } else if (type === FormFieldType.Date || type === FormFieldType.DateTime) {
                const date = new Date(val);
                if (format) {
                    value = formatDate(date, format);
                    rawValue = getSortValue(date, format);
                } else {
                    value = type === FormFieldType.Date
                        ? date.toLocaleDateString()
                        : date.toLocaleTimeString();
                    rawValue = date;
                }
            } else if (type === FormFieldType.Time) {
                rawValue = new Date(val);
                value = rawValue.toLocaleTimeString();
            } else if (columnField.id === "urn:fq:form-field:ticketNumberId"
                    || columnField.id === "urn:fq:form-field:ticket-number") {
                rawValue = parseInt(val, 10);
                return {
                    textDisplay: val,
                    display: (
                        <Link className="btn-none" params={{ chatId: val }} to={Paths.AGENT_CHAT} target="blank">
                            #
                            {val}
                        </Link>
                    ),
                    value: rawValue,
                } as RowData;
            } else if (type === FormFieldType.Number) {
                const display = columnField.id ? TYPE_TO_TYPE[columnField.id as string] : undefined;
                rawValue = parseFloat(val);
                value = formatValueWithDisplay(rawValue, display ?? MetricsDashboardGraphTypeDisplayType.Number).toString();
            } else {
                rawValue = val;
                value = val;
            }
            return {
                display: value,
                value: rawValue,
                textDisplay: value,
            } as RowData;
        });

        const metrics = props.graph.metrics?.map(metric => {
            const metricIndex = props.columnIndexMap[metric.fieldId];
            const data = group.map(row => row.values[metricIndex]).filterNull();
            const metricValue = getMetricValue(data, metric.aggregation);
            const format = metric.display ?? MetricsDashboardGraphTypeDisplayType.Number;
            const textDisplay = formatValueWithDisplay(metricValue, format)?.toString();
            return {
                display: textDisplay,
                textDisplay,
                value: metricValue as number,
            };
        }) ?? [];
        return dimensions.concat(metrics);
    });

    if (props.graph.sort?.isNotEmpty) {
        const dataIndexMap = colData.map(({ fieldId }, index) => ({ fieldId, index })).toIdMap("fieldId", "index");

        const sortProps = props.graph.sort;
        rowData = rowData.sort((left, right) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const sort of sortProps) {
                const valueIndex = dataIndexMap[sort.fieldId]?.toString() as keyof (string | number | undefined);
                const direction = sort.direction === MetricsDashboardSortDirection.Ascending ? -1 : 1;
                const compare = compareValues(left[valueIndex]?.value, right[valueIndex]?.value);
                if (compare !== 0) {
                    return direction * compare;
                }
            }

            return 0;
        });
    }

    return {
        rowsData: rowData,
        columns: cols,
    };
}

export default function Table(props: GraphProps) {
    const theme = useTheme();
    const [sortColumn, setSortColumn] = useState<{direction: Direction, columnIndex: number}>();
    const [pageIndex, setPageIndex] = useState<number>(0);
    const { rowsData, columns } = useMemo(() => createRowsAndColumns(props), [props.data, props.graph]);

    let displayData = rowsData;
    if (sortColumn) {
        const direction = sortColumn.direction === Direction.ASCENDING ? -1 : 1;
        displayData = displayData.sort((left, right) => {
            const leftValue = left[sortColumn.columnIndex];
            const rightValue = right[sortColumn.columnIndex];

            return direction * compareValues(leftValue.value, rightValue.value);
        });
    }

    const itemStart = (pageIndex * PAGE_SIZE);
    displayData = displayData.skip(itemStart).limit(PAGE_SIZE);

    return (
        <div className={theme.isV3 ? Styles["report-table"] : ""}>
            <Title isAdmin={props.isAdmin} graph={props.graph} createMenu={props.createMenu} />

            <div className={theme.isV3 ? Styles["table-container"] : "w-100"}>
                <table className={theme.isV3 ? Styles.table : "table w-100"}>
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th className={
                                    theme.isV3
                                        ? Styles["table-column"]
                                        : `text-blue-light fw-normal ${index === 0 ? "" : "text-center"}`
                                }
                                >
                                    <div className={theme.isV3 ? Styles["table-column-item"] : ""}>
                                        {col}
                                        {theme.isV3 && (
                                            <SortUpDown
                                                direction={index === sortColumn?.columnIndex ? sortColumn?.direction : undefined}
                                                onChange={direction => {
                                                    setSortColumn({
                                                        direction,
                                                        columnIndex: index,
                                                    });
                                                }}
                                            />
                                        )}

                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map((row, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                            <tr key={`row-${index}`}>
                                {row.map((value, columnIndex) => (
                                    <td
                                    // eslint-disable-next-line react/no-array-index-key
                                        key={`row-${index}-${columnIndex}`}
                                        className={theme.isV3
                                            ? Styles["table-cell"]
                                            : `text-gray-600 ${Styles["table-td"]} ${columnIndex === 0 ? "" : "text-center"}`}
                                    >
                                        <div className="text-truncate">
                                            {value.display ?? "-"}
                                        </div>

                                    </td>
                                ))}
                            </tr>
                        ))}

                    </tbody>
                    {rowsData.length > PAGE_SIZE && (
                        <tfoot>
                            <td
                                colSpan={100}
                                className={theme.isV3 ? "" : "text-light text-end pe-2"}
                            >
                                <div className={theme.isV3 ? Styles["table-footer"] : ""}>
                                    <Button
                                        prefixIcon={<RiArrowDropLeftFill />}
                                        variant="ghost-gray"
                                        size="xsmall"
                                        disabled={pageIndex === 0}
                                        onClick={() => setPageIndex(current => current - 1)}
                                    >
                                        Back
                                    </Button>
                                    <span>
                                        Showing
                                        {" "}
                                        {itemStart + 1}
                                        -
                                        {Math.min(itemStart + PAGE_SIZE, rowsData.length)}
                                        {" "}
                                        of
                                        {" "}
                                        {rowsData.length}
                                        {" "}
                                        rows
                                    </span>
                                    <Button
                                        postfixIcon={<RiArrowDropRightFill />}
                                        variant="ghost-gray"
                                        size="xsmall"
                                        disabled={(itemStart + PAGE_SIZE) > rowsData.length}
                                        onClick={() => setPageIndex(current => current + 1)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </td>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}
